import React from "react";
import classNames from "classnames";
import {
    Counterparty,
    getFederalTaxClassificationLabel,
} from "@common/types/counterparty";
import {
    type CounterpartyTinIds,
    useCounterpartyTinQuery,
} from "@api/counterpartyTin.api";
import { DescriptionListItem } from "@design-system/DescriptionList/DescriptionList";
import { TIN_TYPE_LABELS } from "@components/counterparty/tin/tinType";
import { useCanManageCounterpartyTin } from "@components/counterparty/tin/useCanManageCounterpartyTin";
import styles from "./CounterpartyDetailsPanel.module.scss";

const ELIGIBLE_1099_LABELS = new Map<boolean | null, string>([
    [true, "Eligible"],
    [false, "Not eligible"],
    [null, "Not specified"],
]);

const TaxValue: React.FC<{ isEmpty: boolean; children: React.ReactNode }> = ({
    isEmpty,
    children,
}) => (
    <span
        className={classNames(styles.value, { [styles.emptyValue]: isEmpty })}
    >
        {children}
    </span>
);

const TinValue: React.FC<{ ids: CounterpartyTinIds }> = ({ ids }) => {
    const tinQuery = useCounterpartyTinQuery({ ids, enabled: true });

    if (tinQuery.isPending) {
        return <TaxValue isEmpty>Loading…</TaxValue>;
    }
    if (tinQuery.isError) {
        return <TaxValue isEmpty>Failed to load tax ID</TaxValue>;
    }

    const { tinType } = tinQuery.data;
    return (
        <TaxValue isEmpty={!tinType}>
            {tinType ? `${TIN_TYPE_LABELS[tinType]} on file` : "Not on file"}
        </TaxValue>
    );
};

interface Props {
    counterparty: Counterparty;
}

/** Tax classification, tax ID and 1099 rows for the counterparty details list. */
export const CounterpartyTaxRows: React.FC<Props> = ({ counterparty }) => {
    const canManageTin = useCanManageCounterpartyTin();
    const classification = counterparty.federalTaxClassification;
    const { workspaceId } = counterparty;

    return (
        <>
            <DescriptionListItem label="Tax classification">
                <TaxValue isEmpty={!classification}>
                    {getFederalTaxClassificationLabel(classification)}
                </TaxValue>
            </DescriptionListItem>
            {canManageTin && workspaceId && (
                <DescriptionListItem label="Tax ID">
                    <TinValue
                        ids={{ workspaceId, counterpartyId: counterparty.id }}
                    />
                </DescriptionListItem>
            )}
            <DescriptionListItem label="1099">
                <TaxValue isEmpty={counterparty.isEligible1099 === null}>
                    {ELIGIBLE_1099_LABELS.get(counterparty.isEligible1099)}
                </TaxValue>
            </DescriptionListItem>
        </>
    );
};
